<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Keyboard } from 'swiper/modules';
import type { Swiper as SwiperInstance } from 'swiper/types';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import type PhotoSwipe from 'photoswipe';

import 'swiper/css';
import 'photoswipe/style.css';

import ChevronLeftBigIcon from 'src/shared/icons/chevron-left-big.svg';
import propertyPlaceholder from 'src/shared/assets/propertyPlaceholder.png';

import type { MediaItem } from './media-gallery.types';
import styles from './styles.module.css';

const props = withDefaults(
  defineProps<{
    items: MediaItem[];
    initialIndex?: number;
    aspect?: string;
  }>(),
  {
    initialIndex: 0,
    aspect: '16 / 10',
  },
);

const emit = defineEmits<{ change: [index: number] }>();

const mainSwiper = shallowRef<SwiperInstance | null>(null);
const thumbsTrack = ref<HTMLElement | null>(null);
const activeIndex = ref(props.initialIndex);
const lightboxRef = shallowRef<PhotoSwipeLightbox | null>(null);
const pswpRef = shallowRef<PhotoSwipe | null>(null);
const imageDimensions = new Map<number, { width: number; height: number }>();

const hasItems = computed(() => props.items.length > 0);
const showThumbs = computed(() => props.items.length >= 1);
const showNavButtons = computed(() => props.items.length > 1);

const modules = [Keyboard];

function onSwiperReady(s: SwiperInstance): void {
  mainSwiper.value = s;
}

function onSlideChange(s: SwiperInstance): void {
  activeIndex.value = s.activeIndex;
  emit('change', s.activeIndex);
  if (pswpRef.value && pswpRef.value.currIndex !== s.activeIndex) {
    pswpRef.value.goTo(s.activeIndex);
  }
  scrollActiveThumbIntoView(s.activeIndex);
}

function slidePrev(): void {
  mainSwiper.value?.slidePrev();
}

function slideNext(): void {
  mainSwiper.value?.slideNext();
}

function goToIndex(index: number): void {
  mainSwiper.value?.slideTo(index);
}

function scrollActiveThumbIntoView(index: number): void {
  if (!thumbsTrack.value) return;
  const el = thumbsTrack.value.children[index] as HTMLElement | undefined;
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
}

function stopAllIframes(): void {
  if (!pswpRef.value) return;
  const root = pswpRef.value.element;
  if (!root) return;
  root.querySelectorAll('iframe').forEach((frame) => {
    const src = frame.getAttribute('src');
    if (src) {
      frame.setAttribute('src', src);
    }
  });
}

function openLightbox(index: number): void {
  if (!hasItems.value || !lightboxRef.value) return;
  lightboxRef.value.options.dataSource = buildDataSource();
  lightboxRef.value.loadAndOpen(index);
}

function rememberImageDimensions(index: number, event: Event): void {
  const image = event.currentTarget as HTMLImageElement;
  if (!image.naturalWidth || !image.naturalHeight) return;
  imageDimensions.set(index, { width: image.naturalWidth, height: image.naturalHeight });
}

function buildDataSource(): Array<Record<string, unknown>> {
  return props.items.map((item, index) => {
    if (item.kind === 'image') {
      const dimensions = imageDimensions.get(index);
      return {
        src: item.src,
        width: item.width ?? dimensions?.width ?? 1600,
        height: item.height ?? dimensions?.height ?? 1200,
        alt: item.alt ?? '',
      };
    }
    return {
      html: `<div class="pswp__video-wrap"><iframe src="${item.src}" allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`,
      width: 1920,
      height: 1080,
    };
  });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildLightboxThumbsHtml(): string {
  return props.items
    .map((item, index) => {
      const thumbSrc = item.kind === 'image' ? item.src : item.thumb ?? '';
      const isVideo = item.kind === 'video';
      const img = thumbSrc ? `<img src="${escapeHtml(thumbSrc)}" alt="" draggable="false" />` : '';
      const badge = isVideo ? '<span class="pswp-thumbs__play"></span>' : '';
      return `<button type="button" class="pswp-thumbs__btn" data-pswp-thumb-index="${index}" aria-label="Слайд ${index + 1}">${img}${badge}</button>`;
    })
    .join('');
}

function setupLightbox(): void {
  if (lightboxRef.value) {
    lightboxRef.value.destroy();
    lightboxRef.value = null;
  }
  const lightbox = new PhotoSwipeLightbox({
    dataSource: buildDataSource(),
    pswpModule: () => import('photoswipe'),
    showHideAnimationType: 'fade',
    bgOpacity: 0.55,
    initialZoomLevel: 'fit',
    secondaryZoomLevel: 1.5,
    maxZoomLevel: 3,
    closeOnVerticalDrag: true,
    arrowPrevSVG:
      '<svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M37.3301 46.6602L18.6643 27.9944L37.3301 9.32856" stroke="white" stroke-width="4" stroke-linecap="round"/></svg>',
    arrowNextSVG:
      '<svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.6699 9.33984L37.3357 28.0056L18.6699 46.6714" stroke="white" stroke-width="4" stroke-linecap="round"/> </svg> ',
    closeSVG:
      '<svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21.3965 34.6006L34.6025 21.3945" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/><path d="M34.6025 34.6006L21.3965 21.3945" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/><path d="M21.0025 51.3247H35.0018C46.668 51.3247 51.3344 46.6582 51.3344 34.9921V20.9927C51.3344 9.32661 46.668 4.66016 35.0018 4.66016H21.0025C9.33637 4.66016 4.66992 9.32661 4.66992 20.9927V34.9921C4.66992 46.6582 9.33637 51.3247 21.0025 51.3247Z" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  });

  lightbox.on('contentLoad', (event) => {
    const { content } = event;
    const item = props.items[content.index];
    if (item && item.kind === 'video') {
      event.preventDefault();
      content.element = document.createElement('div');
      content.element.className = 'pswp__video-wrap';
      content.element.innerHTML = `<iframe src="${item.src}" allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
    }
  });

  lightbox.on('contentDeactivate', (event) => {
    const root = event.content?.element;
    if (!root) return;
    root.querySelectorAll('iframe').forEach((frame) => {
      const src = frame.getAttribute('src');
      if (src) frame.setAttribute('src', src);
    });
  });

  lightbox.on('uiRegister', () => {
    const pswpInstance = lightbox.pswp;
    if (!pswpInstance) return;
    pswpInstance.ui?.registerElement({
      name: 'media-gallery-thumbs',
      appendTo: 'wrapper',
      onInit: (el, pswp) => {
        el.classList.add('pswp-thumbs');
        if (props.items.length <= 1) {
          el.style.display = 'none';
          return;
        }
        const chevronPrev =
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="12" fill="white" fill-opacity="0.8"/><path d="M14.666 17.332L9.33268 11.9987L14.666 6.66536" stroke="#353C4A" stroke-width="1.5" stroke-linecap="round"/></svg>';
        const chevronNext =
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="12" fill="white" fill-opacity="0.8"/><path d="M9.33398 6.66797L14.6673 12.0013L9.33398 17.3346" stroke="#353C4A" stroke-width="1.5" stroke-linecap="round"/></svg>';
        el.innerHTML = `
          <button type="button" class="pswp-thumbs__nav pswp-thumbs__nav--prev" aria-label="Прокрутить тамбнейлы влево">${chevronPrev}</button>
          <div class="pswp-thumbs__plate">
            <div class="pswp-thumbs__track">${buildLightboxThumbsHtml()}</div>
          </div>
          <button type="button" class="pswp-thumbs__nav pswp-thumbs__nav--next" aria-label="Прокрутить тамбнейлы вправо">${chevronNext}</button>
        `;
        const track = el.querySelector<HTMLElement>('.pswp-thumbs__track');
        const prevBtn = el.querySelector<HTMLButtonElement>('.pswp-thumbs__nav--prev');
        const nextBtn = el.querySelector<HTMLButtonElement>('.pswp-thumbs__nav--next');

        const updateNavVisibility = () => {
          if (!track || !prevBtn || !nextBtn) return;
          const overflowing = track.scrollWidth > track.clientWidth + 1;
          if (!overflowing) {
            prevBtn.style.visibility = 'hidden';
            nextBtn.style.visibility = 'hidden';
            return;
          }
          prevBtn.style.visibility = 'visible';
          nextBtn.style.visibility = 'visible';
          prevBtn.disabled = track.scrollLeft <= 1;
          nextBtn.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
        };

        const updateActive = () => {
          if (!track) return;
          const current = pswp.currIndex;
          track.querySelectorAll<HTMLElement>('[data-pswp-thumb-index]').forEach((btn) => {
            const idx = Number(btn.dataset.pswpThumbIndex);
            btn.classList.toggle('pswp-thumbs__btn--active', idx === current);
            if (idx === current) {
              btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
          });
          window.setTimeout(updateNavVisibility, 250);
        };

        const scrollTrack = (direction: 1 | -1) => {
          if (!track) return;
          const distance = Math.max(120, track.clientWidth * 0.8);
          track.scrollBy({ left: distance * direction, behavior: 'smooth' });
        };

        prevBtn?.addEventListener('click', (event) => {
          event.stopPropagation();
          scrollTrack(-1);
        });
        nextBtn?.addEventListener('click', (event) => {
          event.stopPropagation();
          scrollTrack(1);
        });

        track?.addEventListener('scroll', updateNavVisibility, { passive: true });

        el.addEventListener('click', (event) => {
          const target = (event.target as HTMLElement | null)?.closest<HTMLElement>(
            '[data-pswp-thumb-index]',
          );
          if (!target) return;
          const idx = Number(target.dataset.pswpThumbIndex);
          if (!Number.isNaN(idx)) {
            pswp.goTo(idx);
          }
        });

        pswp.on('change', updateActive);
        pswp.on('resize', updateNavVisibility);
        updateActive();
        window.setTimeout(updateNavVisibility, 0);
      },
    });
  });

  lightbox.on('afterInit', () => {
    pswpRef.value = lightbox.pswp ?? null;
    if (pswpRef.value) {
      pswpRef.value.on('change', () => {
        if (!pswpRef.value || !mainSwiper.value) return;
        if (mainSwiper.value.activeIndex !== pswpRef.value.currIndex) {
          mainSwiper.value.slideTo(pswpRef.value.currIndex);
        }
      });
    }
  });

  lightbox.on('close', () => {
    stopAllIframes();
  });

  lightbox.on('destroy', () => {
    pswpRef.value = null;
  });

  lightbox.init();
  lightboxRef.value = lightbox;
}

function teardownLightbox(): void {
  lightboxRef.value?.destroy();
  lightboxRef.value = null;
  pswpRef.value = null;
}

onMounted(() => {
  if (hasItems.value) {
    setupLightbox();
  }
});

onBeforeUnmount(() => {
  teardownLightbox();
});

watch(
  () => props.items,
  (next) => {
    imageDimensions.clear();
    if (!next.length) {
      teardownLightbox();
      return;
    }
    if (!lightboxRef.value) {
      setupLightbox();
    } else {
      lightboxRef.value.options.dataSource = buildDataSource();
    }
  },
  { deep: true },
);

defineExpose({ openLightbox });
</script>

<template>
  <div :class="styles.gallery">
    <div v-if="hasItems" :class="styles.mainWrap" :style="{ aspectRatio: aspect }">
      <Swiper
        :modules="modules"
        :class="styles.mainSwiper"
        :initial-slide="initialIndex"
        :space-between="0"
        :slides-per-view="1"
        :keyboard="{ enabled: true }"
        @swiper="onSwiperReady"
        @slide-change="onSlideChange"
      >
        <SwiperSlide v-for="(item, index) in items" :key="index" :class="styles.mainSlide">
          <template v-if="item.kind === 'image'">
            <img
              :src="item.src"
              :alt="item.alt ?? ''"
              :class="styles.mainImage"
              draggable="false"
              @load="rememberImageDimensions(index, $event)"
              @click="openLightbox(index)"
            />
          </template>
          <template v-else>
            <div :class="styles.mainVideoPoster" @click="openLightbox(index)">
              <img v-if="item.thumb" :src="item.thumb" alt="" draggable="false" />
              <span :class="styles.playBadge" />
            </div>
          </template>
        </SwiperSlide>
      </Swiper>
    </div>

    <div v-else :class="styles.empty" :style="{ aspectRatio: aspect }">
      <img :src="propertyPlaceholder" alt="" />
    </div>

    <div v-if="showThumbs" :class="styles.thumbsRow">
      <button
        v-if="showNavButtons"
        type="button"
        :class="styles.navBtn"
        aria-label="Предыдущий слайд"
        @click="slidePrev"
      >
        <ChevronLeftBigIcon />
      </button>

      <div ref="thumbsTrack" :class="styles.thumbsTrack">
        <button
          v-for="(item, index) in items"
          :key="`thumb-${index}`"
          type="button"
          :class="[styles.thumbBtn, { [styles.thumbBtnActive]: index === activeIndex }]"
          @click="goToIndex(index)"
        >
          <template v-if="item.kind === 'image'">
            <img :src="item.src" :alt="item.alt ?? ''" draggable="false" />
          </template>
          <template v-else>
            <img v-if="item.thumb" :src="item.thumb" alt="" draggable="false" />
            <span :class="styles.thumbPlay" />
          </template>
        </button>
      </div>

      <button
        v-if="showNavButtons"
        type="button"
        :class="[styles.navBtn, styles.navBtnNext]"
        aria-label="Следующий слайд"
        @click="slideNext"
      >
        <ChevronLeftBigIcon />
      </button>
    </div>
  </div>
</template>
